import {
  ChangeEvent,
  KeyboardEvent,
  RefObject,
  useRef,
  useState,
} from 'react'

import { deepSetObject, snakeToTitleCase, toggleArray } from '@domain-group/javascript-utils'

import type { UseFormPayload } from '@domain-group/react-hooks'

export type TagType = {
  key: string, value: string,
}

type TagParams = {
  fieldKey: string,
  fieldName: string,
  fieldsArray: [TagType],
  formPayload: UseFormPayload,
}

type HandleInputChangeParams = {
  event: ChangeEvent<HTMLInputElement>,
  setInputValue: (value: string) => void,
}

const handleInputChange = (params: HandleInputChangeParams) => {
  const { event, setInputValue } = params
  setInputValue(event.target.value)
}

type AddOrRemoveTagParams = TagParams & {
  tag: TagType,
}

const addTag = (params: AddOrRemoveTagParams) => {
  const {
    fieldKey, fieldName, fieldsArray, formPayload: { entityState, setEntityState }, tag,
  } = params

  if (!fieldsArray){
    // @ts-expect-error TS2345
    const updatedTags = deepSetObject(entityState, `${fieldKey}.${fieldName}`, [tag])
    // @ts-expect-error TS2345
    setEntityState(updatedTags)
  } else if (!fieldsArray.some((item: TagType) => item.key === tag.key)){
    // @ts-expect-error TS2345
    const updatedTags = deepSetObject(entityState, `${fieldKey}.${fieldName}`, [...fieldsArray, tag])
    // @ts-expect-error TS2345
    setEntityState(updatedTags)
  }
}

type HandleKeyDownParams = TagParams & {
  event: KeyboardEvent<HTMLInputElement>,
  formatTagKey: string,
  formatTagValue: string,
  inputValue: string,
  setInputValue: (value: string) => void,
  setShowInput: (value: boolean) => void,
}

const handleKeyDown = (params: HandleKeyDownParams) => {
  const {
    event,
    fieldKey,
    fieldName,
    fieldsArray,
    formPayload,
    formatTagKey,
    formatTagValue,
    inputValue,
    setInputValue,
    setShowInput,
  } = params

  if (event.key === 'Enter' && inputValue.trim()){
    event.preventDefault()
    addTag({
      fieldKey, fieldName, fieldsArray, formPayload, tag: { key: formatTagKey, value: formatTagValue },
    })
    setShowInput(true)
    setInputValue('')
  }
}

const handleFocus = (inputRef: RefObject<HTMLInputElement>) => {
  if (inputRef.current) inputRef.current.focus()
}

type HandleAddButtonParams = TagParams & {
  formatTagKey: string,
  formatTagValue: string,
  inputRef: RefObject<HTMLInputElement>,
  inputValue: string,
  setInputValue: (value: string) => void,
  setShowInput: (value: boolean) => void,
}

const handleAddButton = (params: HandleAddButtonParams) => {
  const {
    fieldKey,
    fieldName,
    fieldsArray,
    formPayload,
    formatTagKey,
    formatTagValue,
    inputRef,
    inputValue,
    setInputValue,
    setShowInput,
  } = params

  if (inputValue.trim()){
    addTag({
      fieldKey, fieldName, fieldsArray, formPayload, tag: { key: formatTagKey, value: formatTagValue },
    })
    setInputValue('')
  } else {
    handleFocus(inputRef)
    setShowInput(true)
    setInputValue('')
  }
}

const handleRemoveTag = (params: AddOrRemoveTagParams) => {
  const {
    fieldKey,
    fieldName,
    fieldsArray,
    formPayload: {
      entityState,
      setEntityState,
    },
    tag,
  } = params

  const filteredTags = fieldsArray.filter((t: TagType) => t.key !== tag.key)

  if (filteredTags.length === 0){
    // remove object from entityState[fieldKey] if the last tag is removed
    const updatedFields = Object.fromEntries(toggleArray(Object.entries(entityState[fieldKey]), [fieldName, undefined]))
    setEntityState({ [fieldKey]: updatedFields })
  } else {
    // remove tag from array
    // @ts-expect-error TS2345
    const updatedTags = deepSetObject(entityState, `${fieldKey}.${fieldName}`, filteredTags)
    // @ts-expect-error TS2345
    setEntityState(updatedTags)
  }
}

type UseTagInputFieldProps = {
  fieldKey: string,
  fieldName: string,
  formPayload: UseFormPayload,
}

function useTagInputField(props: UseTagInputFieldProps) {
  const {
    fieldKey,
    fieldName,
    formPayload: {
      entityState,
    },
    formPayload,
  } = props

  const [inputValue, setInputValue] = useState('')
  const [showInput, setShowInput] = useState(false)

  const inputRef = useRef(null)

  const fieldsArray = entityState[fieldKey][fieldName]
  const hasTags = !!fieldsArray && fieldsArray.length !== 0

  const formatTagKey = inputValue.trim().split(' ').map(word => word.toLowerCase()).join('_')
  const formatTagValue = snakeToTitleCase(formatTagKey)

  return {
    callbacks: {
      handleAddButton: () => handleAddButton({
        fieldKey,
        fieldName,
        fieldsArray,
        formPayload,
        formatTagKey,
        formatTagValue,
        inputRef,
        inputValue,
        setInputValue,
        setShowInput,
      }),
      handleKeyDown: (event: KeyboardEvent<HTMLInputElement>) => handleKeyDown({
        event,
        fieldKey,
        fieldName,
        fieldsArray,
        formPayload,
        formatTagKey,
        formatTagValue,
        inputValue,
        setInputValue,
        setShowInput,
      }),
      handleInputChange: (event: ChangeEvent<HTMLInputElement>) => handleInputChange({
        event,
        setInputValue,
      }),
      handleRemoveTag: (tag: TagType) => handleRemoveTag({
        fieldKey,
        fieldName,
        fieldsArray,
        formPayload,
        tag,
      }),
      setInputValue,
      setShowInput,
    },
    fieldsArray,
    hasTags,
    inputRef,
    inputValue,
    showInput,
  }
}

export default useTagInputField
